// src/pages/UserProfile.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";

import { useParams, Link, useNavigate } from 'react-router-dom';

import api from "../api";
import { normalizeMediaUrl } from "../lib/media";
import PostCard from "../components/PostCard";

/**
 * Extremely forgiving date parser.
 * - Accepts Date, numbers (seconds or ms), numeric strings
 * - Accepts JSON strings or objects with common keys (created_at, date, ts, seconds, _seconds, nanoseconds)
 * - Accepts Firestore-like { seconds, nanoseconds }
 * - Accepts "/Date(123456)/" formats
 * - Accepts "YYYY-MM-DD HH:mm:ss" (assumed UTC)
 * - If parsing fails returns null
 */
function safeParseDate(d) {
  if (d === null || typeof d === 'undefined') return null;

  // if it's already a Date
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;

  // Number (seconds or ms)
  if (typeof d === 'number' && Number.isFinite(d)) {
    // heuristic: if < 1e12 treat as seconds
    const ms = d < 1e12 ? d * 1000 : d;
    const dt = new Date(ms);
    return isNaN(dt.getTime()) ? null : dt;
  }

  // Firestore-like / object timestamp or other object shapes
  if (typeof d === 'object') {
    // common keys
    if (d.seconds || d._seconds || d.seconds === 0 || d._seconds === 0) {
      // handle firestore { seconds, nanoseconds }
      const s = Number(d.seconds ?? d._seconds);
      const ns = Number(d.nanoseconds ?? d._nanoseconds ?? 0);
      if (!Number.isNaN(s)) {
        const ms = s * 1000 + Math.round((ns || 0) / 1e6);
        const dt = new Date(ms);
        return isNaN(dt.getTime()) ? null : dt;
      }
    }

    const candidates = ['date', 'created_at', 'createdAt', 'timestamp', 'ts', 'time', 'value', 'iso'];
    for (const k of candidates) {
      if (typeof d[k] !== 'undefined' && d[k] !== null) {
        const parsed = safeParseDate(d[k]);
        if (parsed) return parsed;
      }
    }

    // fallback: try toString()
    try {
      const s = String(d);
      const parsed = safeParseDate(s);
      if (parsed) return parsed;
    } catch (e) {}
    return null;
  }

  // string handling
  if (typeof d === 'string') {
    const s = d.trim();
    if (!s) return null;

    // numeric string (seconds or ms)
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      return safeParseDate(n);
    }

    // Windows JSON date: /Date(1234567890)/
    const m = s.match(/\/Date\((\-?\d+)(?:[+-]\d+)?\)\//);
    if (m) {
      const n = Number(m[1]);
      return safeParseDate(n);
    }

    // JSON string containing object with date-like fields
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('"') && s.endsWith('"'))) {
      try {
        const obj = JSON.parse(s);
        const parsed = safeParseDate(obj);
        if (parsed) return parsed;
      } catch (e) {
        // fallthrough
      }
    }

    // common un-timezoned format "YYYY-MM-DD HH:mm:ss" -> assume UTC
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
      const t = s.replace(' ', 'T') + 'Z'; // treat as UTC
      const dt = new Date(t);
      if (!isNaN(dt.getTime())) return dt;
    }

    // date-only "YYYY-MM-DD" -> midnight UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const dt = new Date(s + 'T00:00:00Z');
      if (!isNaN(dt.getTime())) return dt;
    }

    // Try plain ISO parse
    const iso = new Date(s);
    if (!isNaN(iso.getTime())) return iso;

    // try adding Z (treat as UTC) if no timezone present
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(s)) {
      const dt = new Date(s + 'Z');
      if (!isNaN(dt.getTime())) return dt;
    }

    // last resort: Date.parse
    const parsed = Date.parse(s);
    if (!isNaN(parsed)) return new Date(parsed);

    return null;
  }

  return null;
}


export default function UserProfile({ API: propAPI, token: propToken, handle: propHandle }) {
  
  const param = useParams();
  const navigate = useNavigate();

  const handle = (propHandle || param.handle || '').toLowerCase();
  const token = propToken || localStorage.getItem('token') || '';
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [profile, setProfile] = useState(null);
  const [counts, setCounts] = useState({ followers: 0, following: 0, posts: 0, reposts: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [followsMe, setFollowsMe] = useState(false);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);


const [locked, setLocked] = useState(false);

   

  const loadHeader = useCallback(async () => {
    try {
      const { data } = await api.get(`/profiles/${encodeURIComponent(handle)}`, { headers });
      setProfile(data.profile || null);
      setCounts({
        followers: (data.stats && data.stats.followers) ?? (data.profile && data.profile.followers_count) ?? 0,
        following: (data.stats && data.stats.following) ?? (data.profile && data.profile.following_count) ?? 0,
        posts: (data.stats && data.stats.posts) ?? (data.profile && data.profile.posts_count) ?? 0,
        reposts: (data.stats && data.stats.reposts) ?? 0
      });

      const serverYouFollow =
        (typeof data.you_follow !== 'undefined' ? data.you_follow :
         typeof data.is_following !== 'undefined' ? data.is_following :
         typeof data.viewer_is_following !== 'undefined' ? data.viewer_is_following :
         (data.profile && typeof data.profile.you_follow !== 'undefined' ? data.profile.you_follow : undefined)
        );
      if (typeof serverYouFollow !== 'undefined') setIsFollowing(Boolean(serverYouFollow));

      const serverFollowsMe =
        (typeof data.follows_me !== 'undefined' ? data.follows_me :
         (data.profile && typeof data.profile.follows_me !== 'undefined' ? data.profile.follows_me : undefined));
      if (typeof serverFollowsMe !== 'undefined') setFollowsMe(Boolean(serverFollowsMe));
    } catch (e) {
      console.error('profile load error', e?.response?.data || e?.message);
      setProfile({ user_id: handle, username: handle });
      setCounts({ followers: 0, following: 0, posts: 0, reposts: 0 });
      setIsFollowing(false);
      setFollowsMe(false);
    }
  }, [handle, headers]);

  const loadFeed = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const qs = (o) => Object.entries(o)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

      const url = `/profiles/${encodeURIComponent(handle)}/feed?${qs({ cursor: reset ? null : cursor, limit: 20 })}`;
      const { data } = await api.get(url, { headers });
      if (data?.locked) {
  setRows([]);
  setCursor(null);
  setLocked(true);
  return;
}
setLocked(false);
      const list = (data?.posts || []).map(p => {
        const media_urls = Array.isArray(p.media_urls) ? p.media_urls : (() => { try { return JSON.parse(p.media_urls || '[]'); } catch { return []; } })();

        // robust parse: prefer sort_ts, then reposted_at, then created_at
        const rawTs = p.sort_ts ?? p.reposted_at ?? p.created_at ?? null;
        const parsedDate = safeParseDate(rawTs);
        const ts = parsedDate ? parsedDate.getTime() : 0;

        return ({
          ...p,
          media_urls,
          ts,
          __raw_time_display: rawTs // keep original available for tooltip if parsing fails
        });
      }).sort((a,b) => b.ts - a.ts);

      setRows(prev => reset ? list : [...prev, ...list]);
      setCursor(data?.nextCursor || null);
    } catch (e) {
      console.error('feed load error', e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  }, [handle, cursor, headers]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadHeader();
        if (!mounted) return;
        await loadFeed(true);
      } catch (err) {}
    })();
    return () => { mounted = false; };
  }, [handle, loadHeader, loadFeed]);

  const toggleFollow = async () => {
    if (!token) return alert('Login required.');
    const prevFollowing = isFollowing;
    const prevFollowers = counts.followers;
    setIsFollowing(!prevFollowing);
    setCounts(c => ({ ...c, followers: prevFollowing ? Math.max(0, c.followers - 1) : c.followers + 1 }));

    try {
      let res;
      if (!prevFollowing) res = await api.post(`/profiles/${encodeURIComponent(handle)}/follow`, {}, { headers });
      else res = await api.delete(`/profiles/${encodeURIComponent(handle)}/follow`, { headers });

      const followedFlag = res?.data?.followed;
      if (typeof followedFlag !== 'undefined') {
        setIsFollowing(Boolean(followedFlag));
        if (!followedFlag) setCounts(c => ({ ...c, followers: Math.max(0, prevFollowers - 1) }));
        else setCounts(c => ({ ...c, followers: Math.max(c.followers, prevFollowers + 1) }));
      } else {
        try { await loadHeader(); } catch {}
      }
    } catch (e) {
      console.error('follow toggle error', e?.response?.data || e?.message);
      setIsFollowing(prevFollowing);
      setCounts(c => ({ ...c, followers: prevFollowers }));
      alert(e?.response?.data?.error || e?.message || 'Follow failed');
    }
  };


  const act = {
    support: async (id) => {
      if (!token) return alert('Login required.');
      setRows(prev => prev.map(r => (r.repost_id === id || r.id === id) ? ({ ...r, support_count: (r.support_count || 0) + 1, _supporting: true }) : r));
      try {
        await api.post(`/posts/${id}/support`, {}, { headers });
        setRows(prev => prev.map(r => (r.repost_id === id || r.id === id) ? ({ ...r, _supporting: false }) : r));
      } catch (e) {
        setRows(prev => prev.map(r => (r.repost_id === id || r.id === id) ? ({ ...r, support_count: Math.max(0, (r.support_count || 1) - 1), _supporting: false }) : r));
        console.error('support error', e?.response?.data || e?.message);
      }
    },
    repost: async (id) => {
      if (!token) return alert('Login required.');
      setRows(prev => prev.map(r => r.id === id ? ({ ...r, repost_count: (r.repost_count || 0) + 1, _reposting: true }) : r));
      try {
        await api.post(`/posts/${id}/repost`, {}, { headers });
        setRows(prev => prev.map(r => r.id === id ? ({ ...r, _reposting: false }) : r));
        alert('Reposted to your profile.');
      } catch (e) {
        setRows(prev => prev.map(r => r.id === id ? ({ ...r, repost_count: Math.max(0, (r.repost_count || 1) - 1), _reposting: false }) : r));
        console.error('repost error', e?.response?.data || e?.message);
        alert(e?.response?.data?.error || 'Repost failed');
      }
    },
    share: async (id) => {
      const url = `${window.location.origin}/post/${id}`;
      if (navigator.share) { try { await navigator.share({ title: profile?.username || 'Varta', url }); } catch {} }
      else { await navigator.clipboard.writeText(url); alert('Link copied!'); }
    }
  };

  const openProfilePic = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [suggestions, setSuggestions] = useState([]);
  const loadSuggestions = useCallback(async () => {
    try {
      const { data } = await api.get(`/profiles/${encodeURIComponent(handle)}/suggestions`, { headers });
      if (Array.isArray(data?.suggestions)) setSuggestions(data.suggestions.slice(0,8));
    } catch (e) { /* ignore */ }
  }, [handle, headers]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  if (!profile) return <div className="page">Loading…</div>;


  return (
    <div className="page">
      <div className="post-card" style={{ marginBottom: 12 }}>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <div style={{ cursor: profile.profile_pic_url ? 'pointer' : 'default' }} onClick={() => openProfilePic(profile.profile_pic_url)}>
            <img
              src={normalizeMediaUrl(profile.profile_pic_url) || '/uploads/default-avatar.png'}
              alt=""
              style={{ width:96, height:96, borderRadius:12, objectFit:'cover' }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize:20, fontWeight:800 }}>
              {profile.username || profile.user_id}
              {followsMe && <span style={{ marginLeft:8, fontSize:12, color:'#059669' }}> · follows you</span>}
            </div>
            <div style={{ color:'#6B7280', marginTop:2 }}>@{profile.user_id}</div>
            {profile.bio && <div style={{ marginTop:8 }}>{profile.bio}</div>}

            <div style={{ marginTop:10, display:'flex', gap:12, alignItems:'center', fontSize:13 }}>
              <div style={{ color: '#111' }}><strong>{counts.posts}</strong> posts</div>
              <div style={{ color: '#111' }}><strong>{counts.followers}</strong> followers</div>
              <div style={{ color: '#111' }}><strong>{counts.following}</strong> following</div>
            </div>
          </div>

          {profile.user_id !== JSON.parse(localStorage.getItem("user") || "{}")?.user_id && (
  <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>

    <button
      className={`btn ${isFollowing ? 'btn-secondary' : 'btn-primary'}`}
      onClick={toggleFollow}
      style={{ minWidth: 96 }}
    >
      {isFollowing ? 'Following' : locked ? 'Request' : 'Follow'}
    </button>

    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>

      <button
        className="btn btn-ghost"
        onClick={() => navigate(`/conversations?user=${profile.user_id}`)}
      >
        Message
      </button>

      <button
        className="btn btn-ghost"
        onClick={() => {
          const reason = window.prompt('Report user (reason):');
          if (!reason) return;
          api.post(`/profiles/${encodeURIComponent(handle)}/report`, { reason }, { headers })
            .then(() => alert('Report submitted'))
            .catch(e => alert(e?.response?.data?.error || 'Report failed'));
        }}
      >
        Report
      </button>

    </div>

  </div>
)}

      </div>
      </div>

      {suggestions.length > 0 && (
        <div className="post-card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight:700, marginBottom:8 }}>People {profile.username ? `followed by ${profile.username}` : 'this user follows'}</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {suggestions.map(s => (
              <div key={s.user_id} style={{ width: 140, border: '1px solid #eee', borderRadius:8, padding:8 }}>
                <Link to={`/u/${s.user_id}`} style={{ display:'flex', gap:8, alignItems:'center', textDecoration:'none', color:'inherit' }}>
                  <img   src={normalizeMediaUrl(s.profile_pic_url) || '/uploads/default-avatar.png'} alt="" style={{ width:40, height:40, borderRadius:8, objectFit:'cover' }} />
                  <div style={{ fontSize:13 }}>
                    <div style={{ fontWeight:700 }}>{s.username || s.user_id}</div>
                    <div style={{ fontSize:12, color:'#6B7280' }}>@{s.user_id}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {locked ? (   <div className="post-card" style={{ textAlign: "center", padding: 40 }}>     🔒 This account is private     <div style={{ marginTop: 6, color: "#6B7280" }}>       Follow this user to see their posts     </div>   </div> ) : (   <div className="feed-col" style={{ marginTop: 12 }}>
        {rows.map((p) => {
          // determine country info from several possible server fields
          
          return (
  <PostCard
    key={`${p.is_repost ? 'r' : 'p'}-${p.id || p.repost_id}-${p.ts}`}
    p={p}
    isOwnProfile={false}
    onSupport={() =>
      act.support(
        p.is_repost
          ? p.repost_id || p.id
          : p.id
      )
    }
    onRepost={() => act.repost(p.id)}
    onShare={() =>
      act.share(
        p.is_repost
          ? p.repost_id || p.id
          : p.id
      )
    }
  />
);
        })}
</div>
)}

      <div className="pager" style={{ marginTop:12 }}>
        {cursor
          ? <button className="btn btn-primary" disabled={loading} onClick={() => loadFeed(false)}>{loading ? 'Loading…' : 'Load more'}</button>
          : <div className="subtle">{rows.length ? 'No more' : 'No posts yet'}</div>}
      </div>
    </div>
  );
}